import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faClock,
  faBook,
  faChartLine,
  faPencilAlt,
  faComments,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';

export default function DashboardPage() {
  const { user } = useAuth();

  return (
    <>
      {/* Top Bar */}
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-brand-950">
          Welcome back, {user?.firstName}!
        </h1>
        <p className="text-sm text-brand-900/60 mt-1">
          Here's what's happening with your learnerships today.
        </p>
      </div>

      {/* Content */}
      <div className="p-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {[
            { label: 'Active Learnerships', value: '2', icon: faBook },
            { label: 'Overall Progress', value: '65%', icon: faChartLine },
            { label: 'Assignments Due', value: '3', icon: faPencilAlt },
            { label: 'Messages', value: '5', icon: faComments },
          ].map((stat) => (
            <div key={stat.label} className="card-surface p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-brand-900/60">{stat.label}</p>
                  <p className="text-3xl font-bold text-brand-950 mt-2">
                    {stat.value}
                  </p>
                </div>
                <FontAwesomeIcon
                  icon={stat.icon}
                  className="h-8 w-8 text-brand-600"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Learning Modules */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Active Learnerships */}
          <div className="card-surface p-6">
            <h2 className="text-lg font-bold text-brand-950 mb-4">
              Active Learnerships
            </h2>
            <div className="space-y-4">
              {[
                {
                  title: 'IT Technical Support',
                  progress: 65,
                  modules: '4/7',
                },
                {
                  title: 'Systems Development',
                  progress: 42,
                  modules: '3/7',
                },
              ].map((learnership) => (
                <div key={learnership.title} className="border border-canvas-300 rounded-2xl p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-brand-950">
                      {learnership.title}
                    </h3>
                    <span className="text-sm font-semibold text-brand-700">
                      {learnership.progress}%
                    </span>
                  </div>
                  <div className="w-full bg-canvas-200 rounded-full h-2 mb-2">
                    <div
                      className="bg-brand-gradient h-2 rounded-full transition-all"
                      style={{ width: `${learnership.progress}%` }}
                    />
                  </div>
                  <p className="text-xs text-brand-900/60">
                    Module {learnership.modules}
                  </p>
                </div>
              ))}
            </div>
            <button className="w-full mt-6 text-center py-2 text-brand-700 hover:text-brand-800 font-semibold text-sm flex items-center justify-center gap-1">
              View all <FontAwesomeIcon icon={faArrowRight} className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Recent Assignments */}
          <div className="card-surface p-6">
            <h2 className="text-lg font-bold text-brand-950 mb-4">
              Recent Assignments
            </h2>
            <div className="space-y-4">
              {[
                { title: 'System Administration Quiz', due: 'In 3 days', status: 'pending' },
                { title: 'Network Configuration Lab', due: 'In 5 days', status: 'pending' },
                { title: 'Programming Challenge', due: 'In 7 days', status: 'pending' },
              ].map((assignment) => (
                <div
                  key={assignment.title}
                  className="flex items-start justify-between border border-canvas-300 rounded-2xl p-4"
                >
                  <div className="flex-1">
                    <h3 className="font-semibold text-brand-950">
                      {assignment.title}
                    </h3>
                    <p className="text-sm text-brand-900/60 mt-1">
                      Due: {assignment.due}
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full flex items-center gap-1">
                    <FontAwesomeIcon icon={faClock} className="h-3 w-3" />
                    Pending
                  </span>
                </div>
              ))}
            </div>
            <button className="w-full mt-6 text-center py-2 text-brand-700 hover:text-brand-800 font-semibold text-sm flex items-center justify-center gap-1">
              View all <FontAwesomeIcon icon={faArrowRight} className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="mt-8 card-surface p-6">
          <h2 className="text-lg font-bold text-brand-950 mb-4">
            Upcoming Events
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { date: 'Aug 22', title: 'Live Class: Systems Security', type: 'Live Class' },
              { date: 'Aug 25', title: 'Module 5 Exam', type: 'Exam' },
              { date: 'Aug 28', title: 'Project Submission Deadline', type: 'Deadline' },
            ].map((event) => (
              <div
                key={event.title}
                className="border border-canvas-300 rounded-2xl p-4"
              >
                <p className="text-sm font-semibold text-brand-700">{event.date}</p>
                <h3 className="font-semibold text-brand-950 mt-2">
                  {event.title}
                </h3>
                <span className="inline-block mt-2 px-2.5 py-1 bg-canvas-200 text-xs font-semibold text-brand-700 rounded-full">
                  {event.type}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
